import React, { useMemo, useState } from 'react';
import { Plus, Download, Search } from 'react-feather';
import { COLORS } from '../../../../utils/colors';
import { AppButton } from '../../../general/AppButton/AppButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import FiledLiensTable from '../FiledLiensTable/FiledLiensTable';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import { FiledLienApiResponse } from '@dill/dill-shared';
import { buildSupplierPath, downloadPDFsAsZip } from '../../../../utils/helpers';
import { AppErrorPage } from '../../../general/AppErrorPage/AppErrorPage';
import moment from 'moment';
import BulkAddManualPrelimNoticesModal from '../BulkAddManualPrelimNoticesModal/BulkAddManualPrelimNoticesModal';
import { getSupplierFiledLiens } from '../../../../redux/services/filedLiensService';

const FiledLiens = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState('');
  const { filedLiens, loadingErrorList, selectedFiledLiens } = useAppSelector(
    (state) => state.filedLiens
  );
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [isBulkAddManualPrelimNoticesModalOpen, setIsBulkAddManualPrelimNoticesModalOpen] =
    useState(false);

  const error = useMemo(() => {
    if (loadingErrorList.length > 0) {
      const errorMessage: any = loadingErrorList.filter((obj) =>
        obj.hasOwnProperty('getSupplierFiledLiens')
      );
      if (errorMessage.length > 0) {
        return errorMessage[0]?.getSupplierFiledLiens;
      }
    }
    return null;
  }, [loadingErrorList]);

  const searchedFiledLiens: FiledLienApiResponse[] = useMemo(() => {
    let filteredFiledLiens: FiledLienApiResponse[] = [];
    if (filedLiens && searchText) {
      const searched = filedLiens.filter((filedLien) => {
        if (
          (filedLien.createdAt &&
            moment(filedLien.createdAt)
              .format('MM/DD/YYYY')
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (filedLien.job &&
            filedLien.job.name.toString().toLowerCase().includes(searchText.toLowerCase())) ||
          (filedLien.job?.jobNumber &&
            filedLien.job.jobNumber.toString().toLowerCase().includes(searchText.toLowerCase())) ||
          (filedLien.job?.buyer?.name &&
            filedLien.job?.buyer?.name
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (filedLien.job?.owner &&
            filedLien.job?.owner.toString().toLowerCase().includes(searchText.toLowerCase())) ||
          (filedLien.job?.GC &&
            filedLien.job?.GC.toString().toLowerCase().includes(searchText.toLowerCase())) ||
          (filedLien.job?.lender &&
            filedLien.job?.lender.toString().toLowerCase().includes(searchText.toLowerCase()))
        ) {
          return true;
        }
        return false;
      });
      filteredFiledLiens = searched;
    } else {
      filteredFiledLiens = filedLiens;
    }

    return filteredFiledLiens;
  }, [filedLiens, searchText]);

  const handleDownloadAll = () => {
    const downloadZipFiles = [] as { url: string; name: string }[];
    selectedFiledLiens.forEach((filedLien) => {
      const createdAtTimeStamp = filedLien?.createdAt
        ? new Date(filedLien.createdAt).getTime()
        : new Date().getTime();
      if (filedLien.pdf) {
        downloadZipFiles.push({
          url: filedLien?.pdf?.url ?? '',
          name: `${filedLien.job?.name ?? ''}-${filedLien.job?.owner ?? ''}-${createdAtTimeStamp}`,
        });
      }
    });
    downloadPDFsAsZip(downloadZipFiles, 'filedLiens');
  };

  const filteredFiledLien: FiledLienApiResponse[] = useMemo(() => {
    if (selectedFilters.length === 0) {
      return searchedFiledLiens;
    }

    return searchedFiledLiens.filter((filedLien) => {
      for (const filter of selectedFilters) {
        if (filter.name === 'amount') {
          const paymentAmount = filedLien.fieldsData?.find(
            (item: any) => item.id === 'paymentAmount'
          );
          if (
            (filter.value?.min &&
              filter.value?.max &&
              (Number(paymentAmount?.value) < Number(filter.value.min) ||
                Number(paymentAmount?.value) > Number(filter.value.max))) ||
            !paymentAmount
          ) {
            return false;
          }
        } else if (filter.name === 'job') {
          if (!filter?.value?.includes(filedLien?.job?.id)) {
            return false;
          }
        }
      }
      return true;
    });
  }, [searchedFiledLiens, selectedFilters]);

  const handleFilterSelect = (selectFilter: any[]) => {
    setSelectedFilters(selectFilter);
  };

  return (
    <div className="h-full p-4 flex ">
      <BulkAddManualPrelimNoticesModal
        open={isBulkAddManualPrelimNoticesModalOpen}
        handleClose={() => {
          setIsBulkAddManualPrelimNoticesModalOpen(false);
          dispatch(getSupplierFiledLiens({ supplierId: currentSupplier?.id || '' }));
        }}
      />
      <div className="flex flex-col px-5 py-4 w-full bg-white">
        <div className="flex justify-between flex-wrap z-20">
          <div className="flex mb-2">
            <AppInputField
              inputFieldStyles={{ width: '200px' }}
              id="search"
              value={searchText}
              placeholder={'Search by recipient or job'}
              icon={<Search size={15} />}
              onTextChange={(text) => {
                setSearchText(text);
              }}
            />
          </div>
          <div className="flex mb-2 items-center justify-center">
            <AppButton
              text="File Lien"
              icon={<Plus color={COLORS.WHITE} />}
              buttonStyles={{ padding: '8px', height: '40px' }}
              onClick={() => {
                navigate(buildSupplierPath(currentSupplier?.id, '/liens/overview/addLien'));
              }}
            />
            {selectedFiledLiens.length > 0 && (
              <AppButton
                text="Bulk Download"
                buttonStyles={{ marginLeft: '5px', padding: '8px', height: '40px' }}
                type="TERTIARY"
                onClick={handleDownloadAll}
                icon={<Download size={20} color={COLORS.TEXT_SECONDARY} />}
              />
            )}
            {/* <AppCustomDropdown
              buttonStyles={{ width: '40px', height: '40px' }}
              inputFieldStyles={{ marginLeft: '5px' }}
              onSelect={(val) => {
                if (val === 'manualUploadPrelimNotice') {
                  navigate(buildSupplierPath(currentSupplier?.id, '/liens/overview/manualAddFiledLien'));
                } else if (val === 'bulkUploadPreviousNotices') {
                  setIsBulkAddManualPrelimNoticesModalOpen(true);
                }
              }}
              options={[
                {
                  value: 'manualUploadPrelimNotice',
                  label: 'Upload Previous Notice',
                  icon: <Upload size={16} />,
                },
                {
                  label: 'Bulk Upload Previous Notices',
                  value: 'bulkUploadPreviousNotices',
                  icon: <Upload size={16} />,
                },
              ]}
              open={openDropdown === 'open'}
              onOpen={() => setOpenDropDown('open')}
              onClose={() => setOpenDropDown('')}
              width="240px"
            /> */}
          </div>
        </div>
        {error ? (
          <AppErrorPage
            title="Error loading  Preliminary Notices."
            contactMessage="Please contact customer support."
            errorMessage={error}
          />
        ) : (
          <div>
            <FiledLiensTable searchedFiledLiens={filteredFiledLien} refresh={refresh} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FiledLiens;
